import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TableBaseAbstractComponent } from '../../../shared/components/table-base-abstract.component';
import { Table, TableModule } from 'primeng/table';
import { TableRequestData } from '../../../shared/models/table/table-request-param';
import { catchError, EMPTY, filter, finalize, Observable, tap } from 'rxjs';
import { TableResponse } from '../../../shared/models/table-response';
import { ReportsApi } from '../../../shared/services/api/reports.api';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import {
  ReportStatus,
  ReportStatusLabels,
} from '../../../shared/enums/report-status.enum';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Card } from 'primeng/card';
import { Paginator } from 'primeng/paginator';
import { PreviewDataWrapperComponent } from '../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../../shared/enums/preview-data-type.enum';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { APP_PATHS } from '../../../core/config/app-paths';
import { ReportNameLabels } from '../../../shared/enums/report-name.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from 'primeng/dynamicdialog';
import { ReportParametersDialog } from './components/report-parameters-dialog/report-parameters-dialog';
import { DictionaryItemDto, Raport } from '../../../shared/models/raport';
import { HttpResponseService } from '../../../core/services/http-error-response.service';
import { EnumLabelPipe } from './pipes/event-type-label.pipe';
import { StatusBadgeClass } from './pipes/status-badge-class.pipe';
import { HttpUtils } from '../../../shared/utils/http.utils';
import { SpinnerService } from '../../../core/services/spinner.service';
import { MessageService } from 'primeng/api';
import { ToastSeverity } from '../../../shared/enums/toast-severity.enum';
import { REPORT_CYCLE_OPTIONS } from '../../../shared/models/raport-generowanie';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { SelectOption } from '../../../shared/models/select-option';
import { PrimeSortEvent } from '../../../shared/models/custom-types';

@Component({
  selector: 'app-admin-panel-reports',
  imports: [
    Button,
    Card,
    DatePicker,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    Paginator,
    PreviewDataWrapperComponent,
    MultiSelect,
    Select,
    TableModule,
    TooltipModule,
    AppIconComponent,
    EnumLabelPipe,
    StatusBadgeClass,
  ],
  templateUrl: './admin-panel-reports.html',
  styleUrl: './admin-panel-reports.scss',
  providers: [ReportsApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelReports
  extends TableBaseAbstractComponent<Raport>
  implements OnInit
{
  @ViewChild('table') override tableRef!: Table;

  protected readonly PreviewDataType = PreviewDataType;
  protected readonly ReportStatus = ReportStatus;
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #spinnerService = inject(SpinnerService);
  readonly #messageService = inject(MessageService);
  #httpResponseService = inject(HttpResponseService);
  protected readonly applicationTypes = input<DictionaryItemDto[]>();

  protected readonly reportNameOptions = Object.entries(ReportNameLabels).map(
    ([key, value]) => ({
      label: value,
      value: key,
    }),
  );

  protected applicationTypeOptions: SelectOption<string>[] = [];
  protected comparisonCycleOptions = REPORT_CYCLE_OPTIONS;

  protected readonly reportStatusOptions = Object.entries(
    ReportStatusLabels,
  ).map(([key, value]) => ({
    label: value,
    value: key,
  }));

  protected tableFilters: {
    rodzajWnioskuCode: string[];
    cykl: string | null;
    name: string | null;
    createdAt: Date | null;
    status: string | null;
    generatedAt: Date | null;
    createdBy: string | null;
  } = {
    rodzajWnioskuCode: [],
    cykl: null,
    name: null,
    createdAt: null,
    status: null,
    generatedAt: null,
    createdBy: null,
  };
  public reportNameLabels = ReportNameLabels;
  public reportStatusLabels = ReportStatusLabels;

  readonly #reportsApi: ReportsApi;
  readonly #router: Router;

  constructor(reportsApi: ReportsApi, router: Router) {
    super();
    this.#reportsApi = reportsApi;
    this.#router = router;
  }

  ngOnInit(): void {
    this.applicationTypeOptions =
      this.applicationTypes()
        ?.map((item) => ({
          label: item.label,
          value: item.key,
        }))
        .filter((el) => el.value !== 'ALL') ?? [];
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<Raport>> => {
    params.sortBy = params.sortBy ?? 'dataUtworzenia';
    params.order = params.order ?? SortDirection.DESC;
    return this.#reportsApi.getReports$(params);
  };

  protected showParameters(report: Raport): void {
    this.#spinnerService.show();
    this.#reportsApi
      .getReportParameters$(report.id)
      .pipe(
        tap((reportWithParams) => {
          this.#dialogService.open(ReportParametersDialog, {
            closable: true,
            header: 'Parametry raportu',
            closeOnEscape: true,
            modal: true,
            dismissableMask: true,
            width: '1000px',
            breakpoints: {
              '960px': '90vw',
              '640px': '90vw',
            },
            inputValues: {
              report: reportWithParams,
              applicationTypeOptions:
                this.applicationTypes()?.map((item) => ({
                  label: item.label,
                  value: item.key,
                })) ?? [],
            },
          });
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania parametrów raportu.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected downloadReport(report: Raport): void {
    this.#spinnerService.show();
    this.#reportsApi
      .downloadReport$(report.id)
      .pipe(
        tap((resp) =>
          HttpUtils.downloadFile(
            resp,
            'xlsx',
            this.#getReportDownloadFileName(report),
          ),
        ),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się pobrać wybranego raportu.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  private deleteReport(report: Raport): void {
    this.#spinnerService.show();
    this.#reportsApi
      .deleteReport$(report.id)
      .pipe(
        tap(() => {
          this.reloadData();
          this.#messageService.add({
            severity: ToastSeverity.SUCCESS,
            summary: 'Poprawnie usunięto raport.',
          });
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się usunąć wybranego raportu.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected onDeleteReportBtnClick(report: Raport): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: 'Czy na pewno chcesz usunąć wybrany raport?',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
      },
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.deleteReport(report)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected generateReport(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.reports}/${APP_PATHS.admin.generate}`,
    ]);
  }

  protected clearFilters(): void {
    this.tableFilters = {
      rodzajWnioskuCode: [],
      cykl: null,
      name: null,
      createdAt: null,
      status: null,
      generatedAt: null,
      createdBy: null,
    };
    this.resetFilters();
  }

  protected onMultiSelectFilterChange(
    values: string[] | null | undefined,
    filterName: string,
  ): void {
    const filterValue = values?.length ? values.join(',') : null;

    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
      pageIndex: 0,
      filters: {
        ...tableRequestData.filters,
        [filterName]: filterValue,
      },
    }));
  }

  public override onPrimeSortChange(event: PrimeSortEvent): void {
    this.tableRequest.update((tableRequestData: TableRequestData) => ({
      ...tableRequestData,
      sortBy: event.field,
      order: event.order === 1 ? SortDirection.ASC : SortDirection.DESC,
    }));
  }

  protected isDownloadAvailable(report: Raport): boolean {
    return report.status.key === ReportStatus.COMPLETED;
  }

  protected getReportAuthor(report: Raport): string {
    return report.userName || '-';
  }

  #getReportDownloadFileName(report: Raport): string {
    const generatedAt = this.#formatReportDate(report.dataWygenerowania);

    return `Raport_${generatedAt}.xlsx`;
  }

  #formatReportDate(value: Date | string | null): string {
    if (!value) {
      return HttpUtils.formatDateToLocalDateString(new Date());
    }

    if (typeof value === 'string') {
      return value.slice(0, 10);
    }

    return HttpUtils.formatDateToLocalDateString(value);
  }
}
